import type { JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  FileTextOutlined,
  LockOutlined,
  MessageOutlined,
  PushpinOutlined,
  ReadOutlined,
  TrophyOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Panel } from '../ds';
import type { MyTasksNotificationDto, TrajectoryGroupDto, TrajectoryItemDto } from '../../lib/api/openingTasks';
import { fmtWhen, wikiLink } from './utils';

/**
 * Правая колонка «Моих задач» на открытии: траектория обучения по этапам
 * (пройден / текущий / закрыт до этапа) и уведомления по открытию.
 */
export function TrajectoryPanel({ groups }: { groups: TrajectoryGroupDto[] }): JSX.Element {
  return (
    <Panel pad={0} icon={<ReadOutlined />} title="Траектория обучения" sub="курсы и статьи идут по этапам открытия">
      {groups.length === 0 ? (
        <div className="fk-ot-empty">Курсы этапов появятся, когда УК их назначит.</div>
      ) : (
        groups.map((g) => (
          <div key={g.stage}>
            <div className="fk-ot-tgroup">
              {g.label}
              {g.state === 'current' ? <span className="fk-ot-here">текущий этап</span> : null}
              {g.state === 'done' ? <span className="fk-ot-lock">· пройден</span> : null}
              {g.state === 'locked' ? (
                <span className="fk-ot-lock">
                  <LockOutlined /> закрыт
                </span>
              ) : null}
            </div>
            {g.items.length === 0 ? (
              <div className="fk-ot-empty" style={{ paddingTop: 6 }}>
                На этом этапе обучения нет.
              </div>
            ) : (
              g.items.map((it) => <TrajectoryRow key={`${it.kind}:${it.id}`} item={it} locked={g.state === 'locked'} stageLabel={g.label} />)
            )}
          </div>
        ))
      )}
    </Panel>
  );
}

function TrajectoryRow({ item, locked, stageLabel }: { item: TrajectoryItemDto; locked: boolean; stageLabel: string }): JSX.Element {
  if (locked) {
    return (
      <div className="fk-ot-trow" data-locked="true">
        <span className="fk-ot-licon">
          <LockOutlined />
        </span>
        <div className="fk-ot-trow-body">
          <div className="fk-ot-ltitle">{item.title}</div>
          <div className="fk-ot-lsub">откроется на этапе «{stageLabel}»</div>
        </div>
        <span />
      </div>
    );
  }
  const pct = item.progressPercent ?? (item.done ? 100 : 0);
  const isCourse = item.kind === 'course';
  const tone = item.done ? 'green' : isCourse ? 'orange' : 'blue';
  const icon = item.done ? isCourse ? <TrophyOutlined /> : <CheckOutlined /> : isCourse ? <ReadOutlined /> : <FileTextOutlined />;
  const sub = isCourse
    ? item.done
      ? 'курс пройден'
      : pct > 0
        ? `пройдено ${Math.round(pct)}%`
        : 'курс'
    : item.done
      ? 'статья прочитана'
      : 'статья';
  const go = item.done ? '' : isCourse ? (pct > 0 ? 'Продолжить' : 'Начать') : 'Читать';
  const body = (
    <>
      <span className="fk-ot-licon" data-tone={tone}>
        {icon}
      </span>
      <div className="fk-ot-trow-body">
        <div className="fk-ot-ltitle">{item.title}</div>
        <div className="fk-ot-lsub">{sub}</div>
        {isCourse && item.progressPercent != null && !item.done ? (
          <div className="fk-ot-bar">
            <span style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
          </div>
        ) : null}
      </div>
      <span className="fk-ot-go">{go}</span>
    </>
  );
  const link = wikiLink(item.link);
  if (!link) return <div className="fk-ot-trow">{body}</div>;
  return link.external ? (
    <a className="fk-ot-trow" href={link.to} target="_blank" rel="noopener noreferrer">
      {body}
    </a>
  ) : (
    <Link className="fk-ot-trow" to={link.to}>
      {body}
    </Link>
  );
}

/** Значок уведомления по смыслу заголовка (бэк не присылает тип). */
function noteIcon(n: MyTasksNotificationDto): [JSX.Element, 'orange' | 'blue' | 'green' | 'red'] {
  const t = `${n.title} ${n.body ?? ''}`.toLowerCase();
  if (t.includes('момент')) return [<PushpinOutlined key="i" />, 'orange'];
  if (/не подходит|отклон|вернул|отказ/.test(t)) return [<WarningOutlined key="i" />, 'red'];
  if (/одобр|подтвердил|переведен|переведён|готов/.test(t)) return [<CheckCircleOutlined key="i" />, 'green'];
  if (n.link && n.link.includes('room=')) return [<MessageOutlined key="i" />, 'blue'];
  return [<BellOutlined key="i" />, 'blue'];
}

export function NotificationsPanel({ items }: { items: MyTasksNotificationDto[] }): JSX.Element {
  const navigate = useNavigate();
  const fresh = items.filter((n) => !n.readAt).length;
  const open = (n: MyTasksNotificationDto): void => {
    const link = wikiLink(n.link);
    if (!link) return;
    if (link.external) {
      window.open(link.to, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(link.to);
    // То же помещение уже открыто — адрес не меняется, прокрутим сами.
    if (/^\/tasks\?(.*&)?room=/.test(link.to)) {
      window.requestAnimationFrame(() => document.getElementById('fk-ot-rooms')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };
  return (
    <Panel pad={0} icon={<BellOutlined />} title="Уведомления" sub={fresh ? `новых: ${fresh}` : 'всё прочитано'}>
      {items.length === 0 ? (
        <div className="fk-ot-empty">Уведомлений по открытию пока нет.</div>
      ) : (
        items.slice(0, 10).map((n) => {
          const [icon, tone] = noteIcon(n);
          const inner = (
            <>
              <span className="fk-ot-licon" data-tone={tone}>
                {icon}
              </span>
              <span style={{ minWidth: 0 }}>
                <b>{n.title}</b>
                {n.body ? <span className="fk-ot-nt-s">{n.body}</span> : null}
                <time dateTime={n.createdAt}>{fmtWhen(n.createdAt)}</time>
              </span>
            </>
          );
          return n.link ? (
            <button key={n.id} type="button" className="fk-ot-nt" data-new={!n.readAt} onClick={() => open(n)}>
              {inner}
            </button>
          ) : (
            <div key={n.id} className="fk-ot-nt" data-new={!n.readAt} data-static="true">
              {inner}
            </div>
          );
        })
      )}
      <div className="fk-ot-pfoot">
        <Link to="/notifications">Настроить уведомления</Link>
      </div>
    </Panel>
  );
}
